import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CustomDataGrid from '../../../../components/DataGrid/CustomDataGrid'
import SmallModal from '../../../../components/Modals/SmallModal'
import API from '../../../../services/API'
import { GlobalContext, TenantSettingsContext } from '../../../../App'

const emptyForm = { name: '', size: '', unitCost: '' }

const StockCatalog = () => {
  const [items, setItems] = useState([])
  const [usage, setUsage] = useState([])
  const [loading, setLoading] = useState(true)
  const [openForm, setOpenForm] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [error, setError] = useState('')
  const { setToast } = useContext(GlobalContext)
  const { orgConfig } = useContext(TenantSettingsContext)
  const currency = orgConfig?.orgSettings?.currency ?? '£'

  const uncostedUsage = usage.filter((x) => !x.costed)

  const nameOptions = useMemo(
    () => [...new Set(usage.map((x) => x.name))].sort(),
    [usage],
  )
  const sizeOptionsForName = useMemo(() => {
    const map = new Map()
    usage.forEach((x) => {
      if (!x.size) return
      if (!map.has(x.name)) map.set(x.name, new Set())
      map.get(x.name).add(x.size)
    })
    return map
  }, [usage])

  const loadItems = () => {
    setLoading(true)
    Promise.all([
      API.get('/stock-catalog'),
      API.get('/stock-catalog/consumable-usage'),
    ])
      .then(([itemsRes, usageRes]) => {
        if (itemsRes.status === 200) setItems(itemsRes.data || [])
        if (usageRes.status === 200) setUsage(usageRes.data || [])
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => loadItems(), [])

  const openAdd = (prefill) => {
    setEditingItem(null)
    setForm(prefill ? { ...emptyForm, ...prefill } : emptyForm)
    setError('')
    setOpenForm(true)
  }

  const openEdit = (item) => {
    setEditingItem(item)
    setForm({ name: item.name, size: item.size || '', unitCost: item.unitCost })
    setError('')
    setOpenForm(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    const payload = {
      name: form.name,
      size: form.size,
      unitCost: parseFloat(form.unitCost),
    }
    const res = editingItem
      ? await API.put(`/stock-catalog/${editingItem._id}`, payload)
      : await API.post('/stock-catalog', payload)

    if (res.status === 200) {
      setOpenForm(false)
      loadItems()
      setToast({ message: editingItem ? 'Catalog item updated' : 'Catalog item added' })
    } else {
      setError(res?.data?.error || 'Unable to save this catalog item.')
    }
  }

  const handleDelete = async (item) => {
    const res = await API.delete(`/stock-catalog/${item._id}`)
    if (res.status === 200) {
      loadItems()
      setToast({ message: 'Catalog item removed' })
    } else {
      setToast({
        message: res?.data?.error || 'Unable to remove this catalog item.',
        severity: 'error',
      })
    }
  }

  return (
    <Stack gap={2}>
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        justifyContent="space-between"
        gap={1}
      >
        <Stack>
          <Typography variant="title2">Stock Catalog</Typography>
          <Typography variant="caption">
            Optional unit costs for consumables — match a load list item's
            name and size here to include it in event cost estimates. Items
            with no match here just show as uncosted, rather than a wrong
            number.
          </Typography>
        </Stack>
        <Button
          variant="contained"
          color="anPurple"
          startIcon={<AddIcon />}
          onClick={() => openAdd()}
        >
          Add Item
        </Button>
      </Stack>
      {uncostedUsage.length > 0 && (
        <Alert severity="warning" icon={<WarningAmberIcon fontSize="inherit" />}>
          <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
            {uncostedUsage.length} item{uncostedUsage.length === 1 ? '' : 's'} used in make-ready load lists {uncostedUsage.length === 1 ? "isn't" : "aren't"} costed yet
          </Typography>
          <Stack direction="row" gap={1} flexWrap="wrap">
            {uncostedUsage.map((x) => (
              <Chip
                key={`${x.name}|${x.size}`}
                label={x.size ? `${x.name} (${x.size})` : x.name}
                size="small"
                onClick={() => openAdd({ name: x.name, size: x.size })}
                title={`Used in: ${x.usedIn.join(', ')}`}
              />
            ))}
          </Stack>
        </Alert>
      )}
      <CustomDataGrid
        rows={items}
        loading={loading}
        getRowId={(row) => row._id}
        disableRowSelectionOnClick
        localeText={{ noRowsLabel: 'No catalog items yet' }}
        columns={[
          { field: 'name', headerName: 'Name', flex: 1, minWidth: 180 },
          { field: 'size', headerName: 'Size', minWidth: 120 },
          {
            field: 'unitCost',
            headerName: 'Unit Cost',
            minWidth: 120,
            renderCell: ({ value }) => `${currency}${Number(value).toFixed(2)}`,
          },
          {
            field: 'actions',
            headerName: '',
            minWidth: 100,
            sortable: false,
            renderCell: ({ row }) => (
              <Stack direction="row">
                <IconButton size="small" onClick={() => openEdit(row)}>
                  <EditIcon fontSize="small" color="anBlue" />
                </IconButton>
                <IconButton size="small" onClick={() => handleDelete(row)}>
                  <DeleteIcon fontSize="small" color="error" />
                </IconButton>
              </Stack>
            ),
          },
        ]}
      />
      <SmallModal open={openForm} onClose={() => setOpenForm(false)}>
        <Grid
          container
          spacing={2}
          component="form"
          onSubmit={handleSubmit}
          padding={1}
        >
          <Grid item xs={12}>
            <Typography variant="title3">
              {editingItem ? 'Edit Catalog Item' : 'Add Catalog Item'}
            </Typography>
          </Grid>
          {error && (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            <Autocomplete
              freeSolo
              options={nameOptions}
              value={form.name}
              onInputChange={(e, value) => setForm((prev) => ({ ...prev, name: value }))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Name"
                  required
                  autoComplete="off"
                  helperText="Matches names already used in make-ready load lists where possible"
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              freeSolo
              options={[...(sizeOptionsForName.get(form.name) || [])]}
              value={form.size}
              onInputChange={(e, value) => setForm((prev) => ({ ...prev, size: value }))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Size"
                  autoComplete="off"
                  helperText="Leave blank if this item has no size variant"
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Unit Cost"
              type="number"
              value={form.unitCost}
              onChange={(e) => setForm((prev) => ({ ...prev, unitCost: e.target.value }))}
              fullWidth
              required
              InputProps={{
                inputProps: { min: 0, step: 0.01 },
                startAdornment: <InputAdornment position="start">{currency}</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Button type="submit" variant="contained" color="success" fullWidth>
              Save
            </Button>
          </Grid>
        </Grid>
      </SmallModal>
    </Stack>
  )
}

export default StockCatalog
